import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import StatusBadge from '../../components/common/StatusBadge';
import DataTable from '../../components/common/DataTable';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { formatDate } from '../../lib/utils';
import { Plus, Clock } from 'lucide-react';
import { toast } from 'sonner';

const LeavePage = ({ isWorker = false }) => {
  const { api, user, isAdmin, isHouseManager } = useAuth();
  const [requests, setRequests] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  
  const [newRequest, setNewRequest] = useState({
    worker_id: 'self',  // 'self' means creating for yourself
    leave_type: 'annual',
    start_date: '',
    end_date: '',
    notes: ''
  });

  const canCreateLeave = isWorker || isHouseManager || isAdmin;

  useEffect(() => {
    fetchRequests();
    if (isHouseManager || isAdmin) {
      fetchWorkers();
    }
  }, [statusFilter]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const res = await api.get('/leave-requests', { params });
      setRequests(res.data);
    } catch (error) {
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkers = async () => {
    try {
      const res = await api.get('/users', { params: { role: 'worker' } });
      // Also include house_manager role so managers can add leave for themselves
      const managersRes = await api.get('/users', { params: { role: 'house_manager' } });
      setWorkers([...res.data, ...managersRes.data]);
    } catch (error) {
      console.error('Failed to fetch workers');
    }
  };

  const handleCreate = async () => {
    try {
      const requestData = {
        leave_type: newRequest.leave_type,
        start_date: newRequest.start_date,
        end_date: newRequest.end_date,
        notes: newRequest.notes
      };
      
      // Only include worker_id if manager is creating for someone else (not "self")
      if ((isHouseManager || isAdmin) && newRequest.worker_id && newRequest.worker_id !== 'self') {
        requestData.worker_id = newRequest.worker_id;
      }
      
      await api.post('/leave-requests', requestData);
      toast.success('Leave request submitted');
      setCreateDialogOpen(false);
      setNewRequest({
        worker_id: 'self',
        leave_type: 'annual',
        start_date: '',
        end_date: '',
        notes: ''
      });
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit leave request');
    }
  };

  const handleReview = async (requestId, decision) => {
    try {
      await api.put(`/leave-requests/${requestId}/review`, null, {
        params: { decision }
      });
      toast.success(`Leave request ${decision}d`);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to review leave request');
    }
  };

  const columns = [
    {
      header: 'Worker',
      accessor: 'worker_name',
      cell: (row) => row.worker_name || 'Unknown'
    },
    {
      header: 'Type',
      accessor: 'leave_type',
      cell: (row) => row.leave_type.charAt(0).toUpperCase() + row.leave_type.slice(1)
    },
    {
      header: 'Start Date',
      cell: (row) => formatDate(row.start_date)
    },
    {
      header: 'End Date',
      cell: (row) => formatDate(row.end_date)
    },
    {
      header: 'Status',
      cell: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex gap-2">
          {(isAdmin || isHouseManager) && row.status === 'pending' && (
            <>
              <Button 
                size="sm" 
                variant="outline"
                className="text-red-500"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReview(row.id, 'decline');
                }}
                data-testid={`decline-leave-${row.id}`}
              >
                Decline
              </Button>
              <Button 
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleReview(row.id, 'approve');
                }}
                data-testid={`approve-leave-${row.id}`}
              >
                Approve
              </Button>
            </>
          )}
        </div>
      )
    }
  ];

  // Filter columns for worker view
  const workerColumns = columns.filter(c => c.header !== 'Worker');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="leave-page">
      <PageHeader 
        title="Leave Requests"
        description={isWorker ? "Manage your leave requests" : "Review and manage staff leave requests"}
        actions={
          canCreateLeave && (
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="request-leave-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  {isWorker ? 'Request Leave' : 'Add Leave Request'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{isWorker ? 'Request Leave' : 'Add Leave Request'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Worker selector for managers */}
                  {(isHouseManager || isAdmin) && (
                    <div className="space-y-2">
                      <Label>For Worker</Label>
                      <Select 
                        value={newRequest.worker_id} 
                        onValueChange={(v) => setNewRequest({...newRequest, worker_id: v})}
                      >
                        <SelectTrigger data-testid="worker-select">
                          <SelectValue placeholder="Select worker (or leave empty for yourself)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">Myself</SelectItem>
                          {workers.map(w => (
                            <SelectItem key={w.id} value={w.id}>
                              {w.first_name} {w.last_name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    <Label>Leave Type</Label>
                    <Select 
                      value={newRequest.leave_type} 
                      onValueChange={(v) => setNewRequest({...newRequest, leave_type: v})}
                    >
                      <SelectTrigger data-testid="leave-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="annual">Annual Leave</SelectItem>
                        <SelectItem value="sick">Sick Leave</SelectItem>
                        <SelectItem value="personal">Personal Leave</SelectItem>
                        <SelectItem value="unpaid">Unpaid Leave</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input 
                        type="date"
                        value={newRequest.start_date}
                        onChange={(e) => setNewRequest({...newRequest, start_date: e.target.value})}
                        data-testid="leave-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input 
                        type="date"
                        value={newRequest.end_date}
                        onChange={(e) => setNewRequest({...newRequest, end_date: e.target.value})}
                        data-testid="leave-end-date"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Notes (optional)</Label>
                    <Textarea 
                      value={newRequest.notes}
                      onChange={(e) => setNewRequest({...newRequest, notes: e.target.value})}
                      placeholder="Any additional information..."
                      rows={3}
                      data-testid="leave-notes"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleCreate} data-testid="submit-leave-btn">Submit Request</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {/* Filter */}
      <Card className="border-border/40 bg-card/50 backdrop-blur-sm">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48" data-testid="leave-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="declined">Declined</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <DataTable 
        columns={isWorker ? workerColumns : columns}
        data={requests}
        emptyMessage="No leave requests found"
      />
    </div>
  );
};

export default LeavePage;
